'use client';

import React, { useState } from 'react';
import {
  QUESTIONS,
  SECTIONS,
  type MCQuestion,
  type OpenQuestion,
  type ScaleQuestion,
  type SelectQuestion,
  type Step2Result,
  buildStep2Result,
} from '../utils/step2Scoring';

interface Step2QuestionnaireProps {
  userName: string;
  onComplete: (result: Step2Result, dob: string) => void;
  onBack: () => void;
}

interface EndQuestions {
  dob: string;
  nightBirth: 'yes' | 'no' | 'not_sure' | '';
  isTwin: 'yes' | 'no' | '';
  email: string;
}

export default function Step2Questionnaire({ userName, onComplete, onBack }: Step2QuestionnaireProps) {
  const [currentSection, setCurrentSection] = useState(1);
  const [openAnswers, setOpenAnswers] = useState<Record<number, string>>({});
  const [selectAnswers, setSelectAnswers] = useState<Record<number, string>>({});
  const [scaleAnswers, setScaleAnswers] = useState<Record<number, number>>({});
  const [mcAnswers, setMcAnswers] = useState<Record<number, string>>({});
  const [errors, setErrors] = useState<Record<number, string>>({});

  // End questions state
  const [showEndQuestions, setShowEndQuestions] = useState(false);
  const [endAnswers, setEndAnswers] = useState<EndQuestions>({ dob: '', nightBirth: '', isTwin: '', email: '' });
  const [endErrors, setEndErrors] = useState<Partial<Record<keyof EndQuestions, string>>>({});

  const totalSections = SECTIONS.length;
  const sectionQuestions = QUESTIONS.filter(q => q.section === currentSection);
  const sectionInfo = SECTIONS.find(s => s.id === currentSection)!;

  const today = new Date().toISOString().split('T')[0];

  function validateSection(): boolean {
    const newErrors: Record<number, string> = {};
    sectionQuestions.forEach(q => {
      if (q.type === 'open') {
        if (!openAnswers[q.id]?.trim()) {
          newErrors[q.id] = 'Please provide an answer.';
        }
      } else if (q.type === 'select') {
        if (!selectAnswers[q.id]) {
          newErrors[q.id] = 'Please select an option.';
        }
      } else if (q.type === 'scale') {
        if (scaleAnswers[q.id] === undefined) {
          newErrors[q.id] = 'Please select a value.';
        }
      } else if (q.type === 'mc') {
        if (!mcAnswers[q.id]) {
          newErrors[q.id] = 'Please select an option.';
        }
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function validateEndQuestions(): boolean {
    const newErrors: Partial<Record<keyof EndQuestions, string>> = {};
    if (!endAnswers.dob) newErrors.dob = 'Date of birth is required.';
    if (!endAnswers.nightBirth) newErrors.nightBirth = 'Please select an option.';
    if (!endAnswers.isTwin) newErrors.isTwin = 'Please select an option.';
    if (!endAnswers.email.trim()) {
      newErrors.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(endAnswers.email.trim())) {
      newErrors.email = 'Please enter a valid email address.';
    }
    setEndErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleNext() {
    if (!validateSection()) return;
    if (currentSection < totalSections) {
      setCurrentSection(s => s + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // All sections done — show end questions
      setShowEndQuestions(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  function handleSubmitEnd() {
    if (!validateEndQuestions()) return;
    const result = buildStep2Result(
      userName,
      endAnswers.dob,
      openAnswers,
      selectAnswers,
      scaleAnswers,
      mcAnswers,
      [] // step1 emotions computed after DOB is known
    );
    onComplete(result, endAnswers.dob);
  }

  function handlePrev() {
    if (showEndQuestions) {
      setShowEndQuestions(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (currentSection > 1) {
      setCurrentSection(s => s - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      onBack();
    }
  }

  const progressPct = showEndQuestions
    ? 100
    : Math.round(((currentSection - 1) / totalSections) * 100);

  return (
    <div className="animate-fade-in space-y-6">
      {/* Header */}
      <div className="rounded-2xl p-6 bg-blue-500/10 border border-blue-500/30">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 border border-blue-500/30 text-blue-300 text-xs font-medium mb-2">
              <span>🧠</span>
              <span>Step 2 — Behavioural Assessment</span>
            </div>
            <h2 className="text-xl font-bold text-white">Behavioural Assessment Questionnaire</h2>
            <p className="text-slate-400 text-sm mt-0.5">{userName}</p>
          </div>
          <div className="text-right">
            {showEndQuestions ? (
              <p className="text-blue-300 text-sm font-semibold">Final Questions</p>
            ) : (
              <>
                <p className="text-slate-500 text-xs mb-1">Section {currentSection} of {totalSections}</p>
                <p className="text-blue-300 text-sm font-semibold">{sectionInfo.title}</p>
              </>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-4">
          <div className="flex justify-between text-xs text-slate-500 mb-1.5">
            <span>Progress</span>
            <span>{progressPct}%</span>
          </div>
          <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 rounded-full transition-all duration-500"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className="flex gap-1.5 mt-2">
            {SECTIONS.map(s => (
              <div
                key={`sec-${s.id}`}
                className={`flex-1 h-1 rounded-full transition-all duration-300 ${
                  showEndQuestions || s.id < currentSection ? 'bg-blue-500' :
                  s.id === currentSection ? 'bg-blue-400' : 'bg-slate-700'
                }`}
              />
            ))}
            {/* End questions dot */}
            <div className={`flex-1 h-1 rounded-full transition-all duration-300 ${showEndQuestions ? 'bg-violet-500' : 'bg-slate-700'}`} />
          </div>
        </div>
      </div>

      {/* ── End Questions ── */}
      {showEndQuestions ? (
        <EndQuestionsPanel
          answers={endAnswers}
          errors={endErrors}
          today={today}
          onChange={(field, value) => {
            setEndAnswers(prev => ({ ...prev, [field]: value }));
            if (endErrors[field]) setEndErrors(prev => ({ ...prev, [field]: undefined }));
          }}
        />
      ) : (
        <>
          {/* Section Info */}
          <div className="flex items-start gap-3 px-1">
            <div className="w-8 h-8 rounded-full bg-blue-500/20 border border-blue-500/30 flex items-center justify-center text-blue-300 text-sm font-bold flex-shrink-0">
              {currentSection}
            </div>
            <div>
              <p className="text-white font-semibold">{sectionInfo.title}</p>
              <p className="text-slate-500 text-xs mt-0.5">{sectionInfo.goal}</p>
            </div>
          </div>

          {/* Questions */}
          <div className="space-y-5">
            {sectionQuestions.map((q) => (
              <div key={`q-${q.id}`} className="dimension-card rounded-2xl p-6">
                <p className="text-slate-500 text-xs mb-2">Q{q.id}</p>
                <p className="text-white text-sm font-medium leading-relaxed mb-4">{q.question}</p>

                {q.type === 'open' && (
                  <OpenTextInput
                    q={q as OpenQuestion}
                    value={openAnswers[q.id] ?? ''}
                    onChange={val => {
                      setOpenAnswers(prev => ({ ...prev, [q.id]: val }));
                      if (errors[q.id]) setErrors(prev => ({ ...prev, [q.id]: '' }));
                    }}
                    error={errors[q.id]}
                  />
                )}

                {q.type === 'select' && (
                  <SelectInput
                    q={q as SelectQuestion}
                    value={selectAnswers[q.id] ?? ''}
                    onChange={val => {
                      setSelectAnswers(prev => ({ ...prev, [q.id]: val }));
                      if (errors[q.id]) setErrors(prev => ({ ...prev, [q.id]: '' }));
                    }}
                    error={errors[q.id]}
                  />
                )}

                {q.type === 'scale' && (
                  <ScaleInput
                    q={q as ScaleQuestion}
                    value={scaleAnswers[q.id]}
                    onChange={val => {
                      setScaleAnswers(prev => ({ ...prev, [q.id]: val }));
                      if (errors[q.id]) setErrors(prev => ({ ...prev, [q.id]: '' }));
                    }}
                    error={errors[q.id]}
                  />
                )}

                {q.type === 'mc' && (
                  <MCInput
                    q={q as MCQuestion}
                    value={mcAnswers[q.id] ?? ''}
                    onChange={val => {
                      setMcAnswers(prev => ({ ...prev, [q.id]: val }));
                      if (errors[q.id]) setErrors(prev => ({ ...prev, [q.id]: '' }));
                    }}
                    error={errors[q.id]}
                  />
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between pt-2">
        <button
          onClick={handlePrev}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-800/60 border border-slate-700/50 text-slate-300 text-sm font-medium hover:bg-slate-700/60 transition-all"
        >
          <span>←</span>
          <span>{currentSection === 1 && !showEndQuestions ? 'Back to Start' : 'Previous'}</span>
        </button>

        {showEndQuestions ? (
          <button
            onClick={handleSubmitEnd}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold transition-all"
          >
            <span>Generate My Report</span>
            <span>→</span>
          </button>
        ) : (
          <button
            onClick={handleNext}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold transition-all"
          >
            <span>{currentSection === totalSections ? 'Continue to Final Questions' : 'Next Section'}</span>
            <span>→</span>
          </button>
        )}
      </div>

      <p className="text-center text-slate-600 text-xs">
        Your answers are used only for this analysis session. The practitioner sees both your open answers and the scored results.
      </p>
    </div>
  );
}

// ─── End Questions Panel ───────────────────────────────────────────────────────

interface EndQuestionsPanelProps {
  answers: {
    dob: string;
    nightBirth: 'yes' | 'no' | 'not_sure' | '';
    isTwin: 'yes' | 'no' | '';
    email: string;
  };
  errors: Partial<Record<'dob' | 'nightBirth' | 'isTwin' | 'email', string>>;
  today: string;
  onChange: (field: 'dob' | 'nightBirth' | 'isTwin' | 'email', value: string) => void;
}

function EndQuestionsPanel({ answers, errors, today, onChange }: EndQuestionsPanelProps) {
  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 px-1">
        <div className="w-8 h-8 rounded-full bg-violet-500/20 border border-violet-500/30 flex items-center justify-center text-violet-300 text-sm font-bold flex-shrink-0">
          ✦
        </div>
        <div>
          <p className="text-white font-semibold">Final Questions</p>
          <p className="text-slate-500 text-xs mt-0.5">All fields are required to generate your report</p>
        </div>
      </div>

      {/* Q1 — Date of Birth */}
      <div className="dimension-card rounded-2xl p-6">
        <p className="text-slate-500 text-xs mb-2">Question 1 of 4</p>
        <p className="text-white text-sm font-medium leading-relaxed mb-4">What is your date of birth?</p>
        <input
          type="date"
          max={today}
          value={answers.dob}
          onChange={e => onChange('dob', e.target.value)}
          className={`w-full bg-slate-900/60 border rounded-xl px-4 py-3 text-slate-200 text-sm focus:outline-none focus:ring-2 transition-all ${
            errors.dob ? 'border-red-500/50 focus:ring-red-500/30' : 'border-slate-700/50 focus:ring-violet-500/30 focus:border-violet-500/50'
          }`}
        />
        {errors.dob && <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1"><span>⚠</span> {errors.dob}</p>}
      </div>

      {/* Q2 — Night Birth */}
      <div className="dimension-card rounded-2xl p-6">
        <p className="text-slate-500 text-xs mb-2">Question 2 of 4</p>
        <p className="text-white text-sm font-medium leading-relaxed mb-4">
          Were you born during the night between <span className="text-violet-300 font-semibold">11:00 pm – 12:00 am</span>?
        </p>
        <div className="flex flex-wrap gap-3">
          {[
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'not_sure', label: 'Not Sure' },
          ].map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange('nightBirth', opt.value)}
              className={`px-6 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                answers.nightBirth === opt.value
                  ? 'bg-violet-500/20 border-violet-500/50 text-violet-200'
                  : 'bg-slate-800/40 border-slate-700/40 text-slate-300 hover:border-slate-600/60 hover:bg-slate-700/40'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {errors.nightBirth && <p className="text-red-400 text-xs mt-2 flex items-center gap-1"><span>⚠</span> {errors.nightBirth}</p>}
      </div>

      {/* Q3 — Twin */}
      <div className="dimension-card rounded-2xl p-6">
        <p className="text-slate-500 text-xs mb-2">Question 3 of 4</p>
        <p className="text-white text-sm font-medium leading-relaxed mb-4">
          Are you a twin?
        </p>
        <div className="flex flex-wrap gap-3">
          {[
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
          ].map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange('isTwin', opt.value)}
              className={`px-6 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                answers.isTwin === opt.value
                  ? 'bg-violet-500/20 border-violet-500/50 text-violet-200'
                  : 'bg-slate-800/40 border-slate-700/40 text-slate-300 hover:border-slate-600/60 hover:bg-slate-700/40'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {errors.isTwin && <p className="text-red-400 text-xs mt-2 flex items-center gap-1"><span>⚠</span> {errors.isTwin}</p>}
      </div>

      {/* Q4 — Email */}
      <div className="dimension-card rounded-2xl p-6">
        <p className="text-slate-500 text-xs mb-2">Question 4 of 4</p>
        <p className="text-white text-sm font-medium leading-relaxed mb-1">
          What is your email address?
        </p>
        <p className="text-slate-500 text-xs mb-4">
          📧 A copy of your results will be emailed to you for reference.
        </p>
        <input
          type="email"
          value={answers.email}
          onChange={e => onChange('email', e.target.value)}
          placeholder="e.g. alex@example.com"
          className={`w-full bg-slate-900/60 border rounded-xl px-4 py-3 text-slate-200 text-sm placeholder-slate-600 focus:outline-none focus:ring-2 transition-all ${
            errors.email ? 'border-red-500/50 focus:ring-red-500/30' : 'border-slate-700/50 focus:ring-violet-500/30 focus:border-violet-500/50'
          }`}
        />
        {errors.email && <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1"><span>⚠</span> {errors.email}</p>}
      </div>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function OpenTextInput({
  q, value, onChange, error,
}: {
  q: OpenQuestion;
  value: string;
  onChange: (v: string) => void;
  error?: string;
}) {
  return (
    <div>
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        rows={4}
        placeholder="Type your answer here…"
        className={`w-full bg-slate-900/60 border rounded-xl px-4 py-3 text-slate-200 text-sm placeholder-slate-600 resize-none focus:outline-none focus:ring-2 transition-all ${
          error ? 'border-red-500/50 focus:ring-red-500/30' : 'border-slate-700/50 focus:ring-blue-500/30 focus:border-blue-500/50'
        }`}
      />
      {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
    </div>
  );
}

function SelectInput({
  q, value, onChange, error,
}: {
  q: SelectQuestion;
  value: string;
  onChange: (v: string) => void;
  error?: string;
}) {
  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {q.options.map(opt => (
          <button
            key={`sel-${opt}`}
            type="button"
            onClick={() => onChange(opt)}
            className={`text-left px-4 py-3 rounded-xl border text-sm transition-all ${
              value === opt
                ? 'bg-blue-500/20 border-blue-500/50 text-blue-200' :'bg-slate-800/40 border-slate-700/40 text-slate-300 hover:border-slate-600/60 hover:bg-slate-700/40'
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
      {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
    </div>
  );
}

function ScaleInput({
  q, value, onChange, error,
}: {
  q: ScaleQuestion;
  value: number | undefined;
  onChange: (v: number) => void;
  error?: string;
}) {
  const steps = Array.from({ length: q.max - q.min + 1 }, (_, i) => q.min + i);
  return (
    <div>
      <div className="flex gap-1.5 flex-wrap">
        {steps.map(n => (
          <button
            key={`scale-${n}`}
            type="button"
            onClick={() => onChange(n)}
            className={`w-10 h-10 rounded-xl border text-sm font-semibold transition-all ${
              value === n
                ? 'bg-blue-500 border-blue-400 text-white' :'bg-slate-800/40 border-slate-700/40 text-slate-300 hover:border-blue-500/40 hover:bg-slate-700/40'
            }`}
          >
            {n}
          </button>
        ))}
      </div>
      <div className="flex justify-between mt-2">
        <span className="text-slate-600 text-xs">{q.minLabel}</span>
        <span className="text-slate-600 text-xs">{q.maxLabel}</span>
      </div>
      {value !== undefined && (
        <p className="text-blue-300 text-xs mt-1">Selected: {value}/10</p>
      )}
      {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
    </div>
  );
}

function MCInput({
  q, value, onChange, error,
}: {
  q: MCQuestion;
  value: string;
  onChange: (v: string) => void;
  error?: string;
}) {
  return (
    <div>
      <div className="space-y-2">
        {q.options.map(opt => (
          <button
            key={`mc-${q.id}-${opt.value}`}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`w-full text-left flex items-start gap-3 px-4 py-3 rounded-xl border text-sm transition-all ${
              value === opt.value
                ? 'bg-blue-500/20 border-blue-500/50 text-blue-100' :'bg-slate-800/40 border-slate-700/40 text-slate-300 hover:border-slate-600/60 hover:bg-slate-700/40'
            }`}
          >
            <span className={`flex-shrink-0 w-6 h-6 rounded-full border flex items-center justify-center text-xs font-bold mt-0.5 ${
              value === opt.value
                ? 'bg-blue-500 border-blue-400 text-white' :'border-slate-600 text-slate-500'
            }`}>
              {opt.value}
            </span>
            <span className="leading-relaxed">{opt.label}</span>
          </button>
        ))}
      </div>
      {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
    </div>
  );
}
